import { ProCard, ProTable } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import { Button, Checkbox, DatePicker, Flex, Form, Input, Popconfirm, Select, Spin, Typography } from 'antd';
import { isEqual, mapValues } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * APIs
 */
import { configs, dConfig, uConfig } from '@/apis/config';

/**
 * Types
 */
import type { ConfigDto, QueryConfigDto } from '@/dtos/config.dto';
import type { ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';

/**
 * Constants
 */
import { RANGE_PICKER_PRESETS } from '@/constants/presets';

const defaultPayload: QueryConfigDto = {
    key_fuzzy: undefined,
    type: undefined,
    is_deleted: false,
    created_at: undefined,
};

const PageRoles: React.FC = () => {
    /**
     * Hooks
     */
    const [formSearch] = Form.useForm();

    const { t } = useTranslation();

    /**
     * States
     */
    const [loading, setLoading] = useSetState<Record<string, boolean>>({});

    /**
     * Paginations
     */
    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        showQuickJumper: true,
        showSizeChanger: true,
        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
    });

    /**
     * Payloads
     */
    const [payload, setPayload] = useSetState<QueryConfigDto>({
        ...defaultPayload,
    });

    /**
     * Requests
     */
    const {
        data: dataConfigs,
        isLoading: loadingConfigs,
        refetch: refetchConfigs,
    } = useQuery({
        queryKey: ['configs', pagination.current, pagination.pageSize, payload],
        retry: false,
        queryFn: () => configs({ page: pagination.current, size: pagination.pageSize, ...payload }),
    });

    /**
     * ChildrenProps
     */
    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: defaultPayload,
        layout: 'inline',
        onFinish: (values) => {
            values.created_at = [
                values.created_at?.[0].startOf('day').format(),
                values.created_at?.[1].endOf('day').format(),
            ];

            const assignPayload = {
                ...defaultPayload,
                ...mapValues(values, (value) => (value === '' ? undefined : value)),
            };

            if (isEqual(payload, assignPayload)) {
                return refetchConfigs();
            }

            setPagination({
                current: 1,
            });

            setPayload(assignPayload);
        },
    };

    const proTableProps: ProTableProps<ConfigDto, QueryConfigDto> = {
        bordered: true,
        columns: [
            {
                dataIndex: 'key',
                title: t('page.configs.key'),
                width: 200,
            },
            {
                dataIndex: 'value',
                ellipsis: true,
                title: t('page.configs.value'),
                renderText: (text) => JSON.stringify(text),
            },
            {
                dataIndex: 'type',
                title: t('type'),
                valueEnum: {
                    private: {
                        status: 'Success',
                        text: t('page.configs.private'),
                    },
                    public: {
                        status: 'Error',
                        text: t('page.configs.public'),
                    },
                },
                width: 200,
            },
            {
                dataIndex: 'created_at',
                title: t('created_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                dataIndex: 'updated_at',
                title: t('updated_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                fixed: 'right',
                key: 'option',
                title: t('actions'),
                valueType: 'option',
                width: 200,
                render: (_, entity) => [
                    <Link key="update" to={`/system-management/configs/${entity.id}/edit`}>
                        {t('edit')}
                    </Link>,
                    <Spin key="delete" size="small" spinning={loading[entity.id] === true}>
                        <Popconfirm
                            title={t('ays')}
                            onConfirm={() => {
                                setLoading({ [entity.id]: true });

                                (entity.is_deleted
                                    ? uConfig({ id: entity.id, is_deleted: false, version: entity.version })
                                    : dConfig(entity.id)
                                )
                                    .then(() => refetchConfigs())
                                    .finally(() => {
                                        setLoading({
                                            [entity.id]: false,
                                        });
                                    });
                            }}
                        >
                            {entity.is_deleted ? (
                                <Typography.Link type="success">{t('restore')}</Typography.Link>
                            ) : (
                                <Typography.Link type="danger">{t('delete')}</Typography.Link>
                            )}
                        </Popconfirm>
                    </Spin>,
                ],
            },
        ],
        dataSource: dataConfigs?.data || [],
        loading: loadingConfigs,
        options: {
            reload: () => refetchConfigs(),
        },
        pagination: {
            ...pagination,
            total: dataConfigs?.meta?.total || 0,
        },
        rowKey: 'id',
        scroll: {
            x: 1200,
        },
        search: false,
        toolbar: {
            actions: [
                <Link key="create" to="/system-management/configs/create">
                    <Button type="primary">{t('create')}</Button>
                </Link>,
            ],
        },
    };

    return (
        <>
            <ProCard className="mb-16">
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap="wrap">
                        <Form.Item name="key_fuzzy" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('page.configs.key')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="type" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                options={[
                                    { label: t('page.configs.private'), value: 'private' },
                                    { label: t('page.configs.public'), value: 'public' },
                                ]}
                                placeholder={t('type')}
                                style={{ width: 100 }}
                            />
                        </Form.Item>

                        <Form.Item label={t('created_at')} name="created_at" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear={true} presets={RANGE_PICKER_PRESETS(t)} />
                        </Form.Item>

                        <Form.Item name="is_deleted" style={{ marginRight: 0 }} valuePropName="checked">
                            <Checkbox>{t('status_deleted')}</Checkbox>
                        </Form.Item>

                        <Button
                            loading={loadingConfigs}
                            type="primary"
                            onClick={() => {
                                formSearch.submit();
                            }}
                        >
                            {t('search')}
                        </Button>

                        <Button
                            onClick={() => {
                                formSearch.resetFields();
                            }}
                        >
                            {t('reset')}
                        </Button>
                    </Flex>
                </Form>
            </ProCard>

            <ProTable {...proTableProps} />
        </>
    );
};

export default PageRoles;
